/**
 * Générateurs des ITEMS NATIONAUX : français, 6e, Compréhension de l'écrit Arachné, campagne 2026.
 * Même contrat que les modules de maths (2026/6e/automatismes.js, le modèle) : un générateur par item, sa
 * fiche en tête (lue sur la CAPTURE de l'item), ITEMS avec la bonne réponse officielle exacte, CATEGORIES,
 * generate.
 *
 * VARIANTES FIXES : une variante par item, posée sur UN NOUVEAU TEXTE (SUPPORT), « Les Piérides », écrit pour
 * TanoEd d'après un mythe grec : les neuf filles du roi Piéros, fières de leur voix, défient les Muses et
 * sont changées en pies (Ovide le raconte au livre V des Métamorphoses, où les Piérides perdent le concours ;
 * la Muse déguisée en bergère, le chant sans faute des Piérides acclamé par la foule et la lyre brisée sont
 * de nous, pour que chaque question trouve son appui). Il est construit comme le texte de
 * septembre (Arachné) : même genre, une légende de métamorphose ; même longueur ; six pages, chacune dans le
 * rôle de la page correspondante :
 *   page 1 : le talent, la célébrité, l'orgueil (l'éloge « élèves des Muses » refusé) ;
 *   page 2 : le défi, puis la déesse déguisée et sa mise en garde ;
 *   page 3 : le mépris et les insultes, la déesse se montre, les réactions des témoins, le défi maintenu ;
 *   page 4 : le concours, décrit dans sa technique ;
 *   page 5 : les deux œuvres (les dieux glorifiés, les dieux moqués), celle des rivales sans défaut ;
 *   page 6 : la colère de la déesse, la punition, la métamorphose et le point commun gardé.
 * Chaque question garde l'attendu, le type, la difficulté et les pièges de l'item : chaque mauvaise réponse
 * officielle a sa raison (fiche de l'item), la variante en fabrique une de même nature sur le nouveau
 * texte. Les renvois aux pages (« Page 2. », « Page 3. ») suivent le nouveau texte.
 * Le texte voyage avec chaque variante (utils/supportLecture.js) : le livre du quiz l'ouvre à la place du
 * texte de septembre. Second chemin (REGLES_EQUIPE § 3) : chaque générateur vérifie que les passages qui
 * servent sa bonne réponse et ses pièges sont sur les pages qu'il cite, sinon il lève une erreur ; Q10
 * calcule l'ordre des évènements sur le texte lui-même.
 * Dans nos énoncés, une personne nommée n'est jamais reprise par « il » ou « elle » (Q09).
 */
import { randomChoice, makeQcm, makeVraiFaux } from '../../../../utils';
import { avecSupportLecture } from '../../../../../utils/supportLecture';

const opts = (label) => ({ niveau: '6eme', matiere: 'francais', attendu: null, label });

/** Le nouveau texte, en six pages comme celui de septembre ; contenu en texte brut, une ligne vide entre les paragraphes. */
export const SUPPORT = {
    id: 'arachne-2026-variante-1',
    titre: 'Les Piérides',
    pages: [
        {
            page: 1,
            title: 'Voici une légende tirée de la mythologie grecque.',
            content: `Le roi Piéros régnait sur une plaine fertile de Macédoine¹. Il avait neuf filles, que l'on appelait les Piérides. Leur père était riche et puissant, mais les neuf sœurs ne lui devaient pas leur célébrité : elles la devaient à leur voix. Les Piérides étaient chanteuses, et leur nom courait de village en village. Dès qu'elles chantaient, les paysans posaient leurs outils, les bergers oubliaient leurs moutons, et les nymphes² sortaient des sources et des bois pour danser autour d'elles. Leurs voix, tantôt douces, tantôt puissantes, ne semblaient jamais se fatiguer.

Les neuf sœurs avaient cependant un défaut : elles étaient terriblement bavardes. Quand elles ne chantaient pas, elles parlaient sans arrêt, et le plus souvent pour vanter leur propre talent. Dans tout le pays, on finit par dire que les Muses elles-mêmes, les déesses du chant, avaient dû leur donner des leçons. Mais ce compliment met les Piérides en colère : elles n'ont jamais eu besoin de maîtresses, répètent-elles, et sûrement pas de ces immortelles qui vivent là-haut, sur le mont Hélicon !

¹ ancien royaume du nord de la Grèce
² petites déesses des sources et des bois`,
        },
        {
            page: 2,
            title: '',
            content: `Un jour de fête, l'aînée des Piérides lance ce défi devant la foule :

– Les Muses ? Qu'elles descendent de leur montagne, si elles l'osent ! Nous sommes neuf, comme elles : un concours de chant dira qui l'emporte. Si nous perdons, elles nous puniront comme elles le voudront !

Du haut de l'Hélicon, Calliope, la plus sage des Muses, entend ces paroles, et sa colère est grande. Elle décide pourtant d'avertir les orgueilleuses avant de les affronter. Elle prend l'apparence d'une bergère : elle noue un foulard usé sur ses cheveux, saisit un bâton de berger et descend dans la plaine en poussant devant elle trois chèvres.

– Mes belles, dit-elle, je ne suis qu'une bergère, mais la vie dans les montagnes m'a appris la sagesse. Écoutez-moi : vous pouvez chercher la gloire et vouloir dépasser toutes les chanteuses de la terre, mais ne vous comparez jamais aux déesses ! Laissez les Muses en paix et ne parlez plus jamais de ce concours : elles oublieront vos paroles insolentes.`,
        },
        {
            page: 3,
            title: '',
            content: `Mais les Piérides éclatent de rire et toisent la bergère, ses chèvres et son bâton ; on lit le mépris dans leurs yeux.

– Retourne donc garder tes chèvres ! lance l'aînée, sans se douter de rien. Que sais-tu du chant, toi qui ne parles qu'à tes bêtes ? Nous n'avons que faire de tes conseils, et rien ne nous fera changer d'avis ! D'ailleurs, si les Muses sont si fortes, pourquoi restent-elles cachées sur leur montagne ? Pourquoi ne viennent-elles pas elles-mêmes, au lieu de nous envoyer une gardeuse de chèvres ?

– Une Muse est devant vous ! répond la bergère, d'une voix qui fait trembler la plaine.

Le foulard glisse, le bâton tombe, les chèvres s'enfuient : à la place de la bergère se dresse Calliope, grande et lumineuse, une couronne d'or sur la tête.

Émerveillées, les nymphes la saluent en chantant son nom. Les bergers, eux, tremblent de peur et tombent à genoux. Seules les Piérides restent debout. Leurs joues rougissent un instant, mais cette rougeur ne dure pas plus longtemps que l'écume d'une vague sur le sable. Les neuf sœurs ne reculent pas : bien au contraire, elles répètent leur défi devant la déesse. Elles ignorent encore ce que leur orgueil va leur coûter.

– Puisque vous le voulez, déclare Calliope, le concours aura lieu.`,
        },
        {
            page: 4,
            title: '',
            content: `Le concours commence aussitôt, dans une prairie au pied de l'Hélicon. Les Piérides se placent d'un côté, Calliope de l'autre. Chacune prend sa lyre³ et l'accorde avec soin : elle tend les sept cordes, les pince l'une après l'autre, puis tourne les chevilles de bois jusqu'à ce que chaque note sonne juste. Bientôt, les doigts courent sur les cordes, si vite que l'œil ne peut plus les suivre. Les chanteuses respirent lentement, profondément, pour que leur souffle reste long et régulier. Les Piérides chantent d'une seule voix : les voix graves soutiennent les voix aiguës, qui montent très haut sans jamais trembler. Calliope leur répond seule, et sa voix remplit la vallée entière. Les notes s'élèvent et retombent comme les vagues d'une mer agitée par la tempête ; l'écho des montagnes les renvoie au loin. Dans la foule, plus personne n'ose parler, ni même bouger. Peu à peu, au fil des chants, de longues histoires prennent vie : ceux qui écoutent croient voir les personnages passer sous leurs yeux.

³ instrument de musique à cordes`,
        },
        {
            page: 5,
            title: '',
            content: `Les deux chants racontent des aventures très anciennes. Calliope chante la puissance des dieux de l'Olympe : Zeus, le roi du ciel, qui lance la foudre ; Héra, son épouse, assise sur un trône d'or ; Athéna, casquée et armée de sa lance ; Apollon, le dieu de la lumière, qui conduit chaque jour le char du soleil. Dans son chant, chaque dieu apparaît fort, juste et généreux envers les humains.

Les Piérides, elles, chantent une tout autre histoire. Elles racontent qu'un jour, terrifiés par un monstre gigantesque, les dieux de l'Olympe se sont enfuis jusqu'en Égypte et se sont cachés dans des corps d'animaux : Zeus en bélier, Apollon en corbeau, Artémis en chatte. Leur chant se moque des dieux et les montre peureux et ridicules.

Pour finir, les neuf sœurs reprennent ensemble leur refrain : pas une note de trop, pas une note de travers. Même une Muse n'y trouverait rien à corriger.`,
        },
        {
            page: 6,
            title: '',
            content: `Quand la dernière note s'éteint, la foule acclame les neuf sœurs. Calliope ne supporte pas de voir ses rivales triompher. Pâle de colère, elle arrache la lyre des mains de l'aînée et la brise contre un rocher.

Humiliées, les Piérides se mettent à crier toutes ensemble :

– Plutôt mourir que de nous taire devant toi !

– Vous ne mourrez pas, malheureuses ! leur répond la déesse. Vous aimez tant faire entendre votre voix ? Eh bien, vous bavarderez pour toujours, vous et toutes celles qui naîtront de vous, jusqu'à la fin des temps !

Puis Calliope remonte vers l'Hélicon. Juste avant de disparaître, elle effleure de la main la bouche des neuf sœurs. Aussitôt, leurs bras se couvrent de plumes, leurs lèvres durcissent en becs pointus, et les voilà qui s'envolent : les Piérides sont devenues des pies. Elles ont gardé leur goût pour le bavardage : aujourd'hui encore, perchées dans les arbres, elles jacassent du matin au soir.

Texte écrit pour TanoEd, d'après un mythe grec.`,
        },
    ],
};

/** Second chemin (REGLES_EQUIPE § 3) : un texte qui ne sert plus la question lève une erreur au lieu de partir. */
const garde = (condition, message) => {
    if (!condition) throw new Error(`Item national de français 6e (Arachné, variante), ${message}`);
};
/** Les passages [page, extrait] qui servent une question sont bien sur les pages citées. */
const appuis = (question, passages) => passages.forEach(([page, extrait]) => {
    garde(SUPPORT.pages[page - 1]?.content.includes(extrait), `${question} : « ${extrait} » absent de la page ${page}`);
});
/** Le QCM d'un item sur le nouveau texte : quatre choix en texte, le juste en premier (makeQcm les mélange). */
const qcmSurLeTexte = (choix, texte, label) => {
    garde(choix.length === 4 && new Set(choix).size === 4, `${label} : quatre choix différents`);
    return avecSupportLecture(makeQcm(choix, choix[0], texte, opts(label)), SUPPORT);
};

/**
 * Q01 : le titre qui convient au texte.
 * Item : « Un seul de ces titres peut convenir au texte. Lequel ? » (Rendre compte du sens global et
 *   identifier la visée) ; La punition d'une orgueilleuse (juste), La fabrication d'une tapisserie,
 *   La trahison de la déesse Minerve, La mort tragique d'Arachné.
 * Texte : tout le récit, l'orgueil des Piérides (« avertir les orgueilleuses », page 2 ; « ce que leur
 *   orgueil va leur coûter », page 3) puis leur punition (« les Piérides sont devenues des pies », page 6).
 * Erreurs : un passage long et détaillé, mais secondaire, pris pour le sujet : le travail au métier à tisser
 *   de la page 4 (La fabrication d'une tapisserie), ici l'accord et le jeu des lyres de la page 4 (L'art de
 *   jouer de la lyre) ; un contresens sur la déesse, qui se déguise pour avertir, non pour tromper, avant de
 *   punir (La trahison de la Muse Calliope) ; un contresens sur la fin : Arachné menace de mourir mais vit,
 *   changée en araignée ; ici les Piérides crient « Plutôt mourir… » et la déesse répond « Vous ne mourrez
 *   pas » (La mort tragique des Piérides).
 */
const L01 = 'Le titre qui convient au texte';
const genQ01 = () => {
    appuis('Q01', [[2, 'avertir les orgueilleuses'], [3, 'ce que leur orgueil va leur coûter'],
        [6, 'les Piérides sont devenues des pies'], [4, 'tourne les chevilles de bois'], [6, 'Plutôt mourir'],
        [6, 'Vous ne mourrez pas']]);
    return qcmSurLeTexte(
        ['La punition de neuf orgueilleuses', 'L\'art de jouer de la lyre', 'La trahison de la Muse Calliope',
            'La mort tragique des Piérides'],
        '<p>Un seul de ces titres peut convenir au texte. Lequel ?</p>', L01);
};

/**
 * Q02 : l'art des deux rivales.
 * Item : « Arachné et Minerve sont toutes deux expertes dans l'art de… » (Retrouver une information
 *   explicite plus ou moins aisément repérable) ; tisser. (juste), peindre., se transformer., se coiffer.
 * Texte : « Les Piérides étaient chanteuses » et « les Muses elles-mêmes, les déesses du chant » (page 1),
 *   puis le concours de chant (pages 2 à 5).
 * Erreurs : un art voisin que le texte fait voir sans que les rivales le pratiquent : les teintes et les
 *   tableaux du tissage (peindre.), ici les nymphes qui dansent au son des voix, page 1 (danser.) ; une
 *   action du récit prise pour un art : la déesse se déguise, l'héroïne est changée en animal (se
 *   transformer., gardé) ; un détail du déguisement : les faux cheveux blancs (se coiffer.), ici les trois
 *   chèvres de la bergère, page 2 (garder des chèvres.).
 */
const L02 = 'L\'art des deux rivales';
const genQ02 = () => {
    appuis('Q02', [[1, 'Les Piérides étaient chanteuses'], [1, 'les Muses elles-mêmes, les déesses du chant'],
        [1, 'les nymphes² sortaient des sources et des bois pour danser'], [2, 'Elle prend l\'apparence d\'une bergère'],
        [2, 'en poussant devant elle trois chèvres'], [6, 'les Piérides sont devenues des pies']]);
    return qcmSurLeTexte(['chanter.', 'danser.', 'se transformer.', 'garder des chèvres.'],
        '<p>Les Piérides et les Muses sont toutes expertes dans l\'art de…</p>', L02);
};

/**
 * Q03 : l'apparence que prend la déesse.
 * Item : « Page 2. Minerve prend l'apparence d'une… » (Retrouver une information explicite plus ou moins
 *   aisément repérable) ; vieille femme. (juste), araignée., déesse., brodeuse.
 * Texte : « Elle prend l'apparence d'une bergère » (page 2).
 * Erreurs : l'animal de la fin du récit (araignée., ici pie.) ; ce qu'est vraiment la déesse, qui se montre
 *   à la page 3 (déesse., gardé : Calliope est l'une des Muses, « les déesses du chant ») ; le talent de
 *   l'héroïne, nommé dans le texte (brodeuse., ici chanteuse. : « Les Piérides étaient chanteuses »).
 */
const L03 = 'L\'apparence que prend la déesse';
const genQ03 = () => {
    appuis('Q03', [[2, 'Calliope, la plus sage des Muses'], [2, 'Elle prend l\'apparence d\'une bergère'],
        [6, 'les Piérides sont devenues des pies'], [1, 'les déesses du chant'], [3, 'se dresse Calliope'],
        [1, 'Les Piérides étaient chanteuses']]);
    return qcmSurLeTexte(['bergère.', 'pie.', 'déesse.', 'chanteuse.'],
        '<p>Page 2. Calliope prend l\'apparence d\'une…</p>', L03);
};

/**
 * Q04 : le conseil de la déesse déguisée.
 * Item : « Page 2. Minerve, sous l'apparence d'une vieille femme, conseille à Arachné… » (Retrouver une
 *   information explicite plus ou moins aisément repérable) ; de demander pardon. (juste), de faire des
 *   expériences., de surpasser les autres., d'avoir de l'ambition.
 * Texte : « Laissez les Muses en paix et ne parlez plus jamais de ce concours : elles oublieront vos
 *   paroles insolentes. » (page 2), que la bonne réponse reformule (de renoncer au concours.), comme
 *   « supplier Minerve d'oublier ton orgueil […], elle te pardonnera » devenait « de demander pardon ».
 * Erreurs : des mots du discours pris hors de leur sens : ce que la vieille femme dit d'elle-même, « Les
 *   années leur donnent beaucoup d'expérience » (de faire des expériences.), ici « la vie dans les
 *   montagnes m'a appris la sagesse » (de vivre dans les montagnes.) ; ce qu'elle permet sans le
 *   conseiller, « tu peux avoir l'ambition de surpasser toutes les mortelles » (de surpasser les autres.,
 *   d'avoir de l'ambition.), ici « vous pouvez chercher la gloire et vouloir dépasser toutes les
 *   chanteuses de la terre » (de dépasser toutes les chanteuses., de chercher la gloire.).
 */
const L04 = 'Le conseil de la déesse déguisée';
const genQ04 = () => {
    appuis('Q04', [[2, 'Laissez les Muses en paix et ne parlez plus jamais de ce concours'],
        [2, 'la vie dans les montagnes m\'a appris la sagesse'],
        [2, 'vous pouvez chercher la gloire et vouloir dépasser toutes les chanteuses de la terre']]);
    return qcmSurLeTexte(
        ['de renoncer au concours.', 'de vivre dans les montagnes.', 'de dépasser toutes les chanteuses.',
            'de chercher la gloire.'],
        '<p>Page 2. Calliope, sous l\'apparence d\'une bergère, conseille aux Piérides…</p>', L04);
};

/**
 * Q05 : la réaction devant la déesse.
 * Item : « Page 3. Lorsqu'elle découvre Minerve, la réaction d'Arachné montre… » (Mettre en relation des
 *   informations et établir des inférences) ; sa détermination. (juste), son admiration., son respect.,
 *   sa timidité. Sans pronom : « Lorsque les Piérides découvrent Calliope, leur réaction montre… ».
 * Texte : « Seules les Piérides restent debout. […] Les neuf sœurs ne reculent pas : bien au contraire,
 *   elles répètent leur défi devant la déesse. » (page 3) : un défi maintenu, que la réponse nomme
 *   (leur entêtement.), comme « la brodeuse persiste dans son entreprise » donnait la détermination.
 * Erreurs : la réaction des autres témoins prise pour celle de l'héroïne : les nymphes impressionnées
 *   (son admiration.), ici « Émerveillées, les nymphes la saluent » (leur admiration.) ; les femmes qui
 *   s'inclinent avec respect (son respect.), ici les bergers qui « tremblent de peur et tombent à genoux »
 *   (leur peur.) ; la rougeur d'un instant lue comme de la gêne (sa timidité., ici leur timidité. : « Leurs
 *   joues rougissent un instant »).
 */
const L05 = 'La réaction devant la déesse';
const genQ05 = () => {
    appuis('Q05', [[3, 'se dresse Calliope'], [3, 'Seules les Piérides restent debout'],
        [3, 'Les neuf sœurs ne reculent pas : bien au contraire, elles répètent leur défi'],
        [3, 'Émerveillées, les nymphes la saluent'], [3, 'tremblent de peur et tombent à genoux'],
        [3, 'Leurs joues rougissent un instant']]);
    return qcmSurLeTexte(['leur entêtement.', 'leur admiration.', 'leur peur.', 'leur timidité.'],
        '<p>Page 3. Lorsque les Piérides découvrent Calliope, leur réaction montre…</p>', L05);
};

/**
 * Q06 : le sujet des deux œuvres.
 * Item : « Les deux toiles brodées ont pour sujet principal… » (Mettre en relation des informations et
 *   établir des inférences) ; les dieux. (juste), les humains., un coucher de soleil., la création du monde.
 * Texte : « Calliope chante la puissance des dieux de l'Olympe » ; « Leur chant se moque des dieux »
 *   (page 5) : deux chants sur les dieux, l'un pour les glorifier, l'autre pour s'en moquer.
 * Erreurs : un élément secondaire d'une seule des deux œuvres : les humains, victimes des dieux sur la toile
 *   d'Arachné (les humains.), ici les animaux dont les dieux prennent la forme dans le chant des Piérides
 *   (les animaux.) ; une image des comparaisons prise pour un sujet : le ciel au lever du jour, l'arc-en-ciel
 *   sous le soleil (un coucher de soleil.), ici une « mer agitée par la tempête » (page 4) et l'écume d'une
 *   vague (page 3) (une tempête en mer.) ; un grand sujet de mythe que le texte ne traite pas, appelé par
 *   les « temps fort anciens » (la création du monde., gardé : « des aventures très anciennes »).
 */
const L06 = 'Le sujet des deux œuvres';
const genQ06 = () => {
    appuis('Q06', [[5, 'Calliope chante la puissance des dieux'], [5, 'Leur chant se moque des dieux'],
        [5, 'se sont cachés dans des corps d\'animaux'], [4, 'comme les vagues d\'une mer agitée par la tempête'],
        [3, 'l\'écume d\'une vague'], [5, 'des aventures très anciennes']]);
    return qcmSurLeTexte(['les dieux.', 'les animaux.', 'une tempête en mer.', 'la création du monde.'],
        '<p>Les deux chants ont pour sujet principal…</p>', L06);
};

/**
 * Q07 : les causes de la colère de la déesse.
 * Item : « Cocher vrai ou faux. Arachné provoque la colère de Minerve car… » (Mettre en relation des
 *   informations et établir des inférences) ; tableau Vrai / Faux de quatre lignes : Arachné a lancé un
 *   défi à une déesse. (Vrai), Arachné a déchiré la toile de Minerve. (Faux), Arachné a représenté la
 *   puissance des dieux. (Faux), Arachné a brodé une toile parfaite. (Vrai).
 * Texte : le défi, puis « sa colère est grande » (page 2) ; le refrain sans faute, « Même une Muse n'y
 *   trouverait rien à corriger » (page 5), et « Calliope ne supporte pas de voir ses rivales triompher »
 *   (page 6), qui fait du chant parfait une cause de la colère, comme « furieuse du succès de sa rivale ».
 * Erreurs (les deux lignes Faux) : les rôles inversés : c'est Minerve qui déchire la toile d'Arachné, ici
 *   Calliope qui brise la lyre de l'aînée (Les Piérides ont brisé la lyre de Calliope.) ; les deux œuvres
 *   confondues : la puissance des dieux est sur la toile de Minerve, ici dans le chant de Calliope (Les
 *   Piérides ont chanté la puissance des dieux.). Mêmes colonnes et même ordre : Vrai, Faux, Faux, Vrai.
 */
const L07 = 'Pourquoi la déesse se met en colère';
const genQ07 = () => {
    appuis('Q07', [[2, 'l\'aînée des Piérides lance ce défi'], [2, 'sa colère est grande'],
        [5, 'Même une Muse n\'y trouverait rien à corriger'], [6, 'Calliope ne supporte pas de voir ses rivales triompher'],
        [6, 'elle arrache la lyre des mains de l\'aînée et la brise'], [5, 'Calliope chante la puissance des dieux']]);
    const lignes = [
        'Les Piérides ont lancé un défi aux Muses.',
        'Les Piérides ont brisé la lyre de Calliope.',
        'Les Piérides ont chanté la puissance des dieux.',
        'Les Piérides ont chanté à la perfection.',
    ];
    return avecSupportLecture(makeVraiFaux(lignes, [0, 1, 1, 0],
        '<p>Cocher vrai ou faux.</p><p>Les Piérides provoquent la colère de Calliope car…</p>',
        { ...opts(L07), colonnes: ['Vrai', 'Faux'] }), SUPPORT);
};

/**
 * Q08 : la punition.
 * Item : « Minerve punit Arachné en la condamnant à… » (Retrouver une information explicite plus ou moins
 *   aisément repérable) ; être une araignée. (juste), disparaitre., mourir., être misérable.
 * Texte : « les Piérides sont devenues des pies » (page 6).
 * Erreurs : ce que fait la déesse prêté à l'héroïne, « avant de disparaître » (disparaître., gardé : ici
 *   « Juste avant de disparaître », Calliope ; écrit comme dans notre texte) ; un contresens : Arachné
 *   menace de mourir et la déesse la laisse vivre (mourir., gardé : « Plutôt mourir… », « Vous ne mourrez
 *   pas ») ; une insulte prise pour la punition, « Vis donc, misérable ! » (être misérable.), ici « Vous ne
 *   mourrez pas, malheureuses ! » (être malheureuses.).
 */
const L08 = 'La punition';
const genQ08 = () => {
    appuis('Q08', [[6, 'les Piérides sont devenues des pies'], [6, 'Juste avant de disparaître'],
        [6, 'Plutôt mourir'], [6, 'Vous ne mourrez pas, malheureuses !']]);
    return qcmSurLeTexte(['être des pies.', 'disparaître.', 'mourir.', 'être malheureuses.'],
        '<p>Calliope punit les Piérides en les condamnant à…</p>', L08);
};

/**
 * Q09 : le point commun avec l'animal.
 * Item : « Le point commun entre Arachné et l'araignée est qu'elles… » (Mettre en relation des
 *   informations et établir des inférences) ; tissent des toiles. (juste), font peur., sont laides.,
 *   sont immortelles. Sans pronom pour les Piérides : « Le point commun entre les Piérides et les pies
 *   est… », chaque choix commençant par « de », comme ceux de Q04.
 * Texte : « elles étaient terriblement bavardes » (page 1) ; « Elles ont gardé leur goût pour le
 *   bavardage : aujourd'hui encore, perchées dans les arbres, elles jacassent du matin au soir. » (page 6).
 * Erreurs : une idée reçue sur l'animal, absente du texte (font peur., ici de voler des objets brillants. :
 *   la pie voleuse) ; l'apparence de l'animal seul, jamais celle de l'héroïne (sont laides., ici d'être
 *   noires et blanches. ; le texte ne dit pas la couleur des pies) ; la punition qui dure toujours, étendue
 *   aux descendantes, et les « immortelles » de la page 1, lues comme une vie sans fin (sont immortelles.,
 *   ici d'être immortelles. : « jusqu'à la fin des temps »).
 */
const L09 = 'Le point commun avec l\'animal';
const genQ09 = () => {
    appuis('Q09', [[1, 'elles étaient terriblement bavardes'], [6, 'Elles ont gardé leur goût pour le bavardage'],
        [6, 'elles jacassent du matin au soir'], [6, 'jusqu\'à la fin des temps'], [1, 'ces immortelles']]);
    // les deux pièges restent faux : le texte ne dit ni la couleur des pies, ni qu'elles volent quoi que ce soit
    garde(SUPPORT.pages.every((p) => !/\b(noire?s?|blanc(he)?s?|vol(s|e|es|ent|er|ait|aient)?|voleuses?)\b/i.test(p.content)),
        'Q09 : le texte ne doit dire ni la couleur des pies ni un vol');
    return qcmSurLeTexte(
        ['de bavarder sans cesse.', 'de voler des objets brillants.', 'd\'être noires et blanches.', 'd\'être immortelles.'],
        '<p>Le point commun entre les Piérides et les pies est…</p>', L09);
};

/**
 * Q10 : l'ordre des évènements.
 * Item : « Remettre les évènements dans l'ordre du texte. » (Rendre compte du sens global et identifier la
 *   visée) ; tableau de quatre lignes, colonnes 1ᵉʳ évènement à 4ᵉ évènement, les lignes dans l'ordre
 *   inverse du récit : La métamorphose en araignée (4ᵉ), Le concours de tissage (3ᵉ), La visite d'une
 *   vieille femme (2ᵉ), Les vantardises d'Arachné (1ᵉʳ).
 * Texte : les vantardises (pages 1 et 2), la visite de la bergère (pages 2 et 3), le concours (pages 4
 *   et 5), la métamorphose en pies (page 6). Le rang de chaque ligne est calculé sur le texte (la place de
 *   son passage), puis comparé à la disposition de l'item.
 * Erreurs : l'ordre des lignes, inverse de celui du récit, recopié tel quel ; gardé : les lignes dans le
 *   même ordre inverse (celui de la capture), avec les mêmes colonnes, un rang par évènement.
 */
const L10 = 'L\'ordre des évènements';
const COLONNES_Q10 = ['1ᵉʳ évènement', '2ᵉ évènement', '3ᵉ évènement', '4ᵉ évènement'];
const genQ10 = () => {
    const evenements = [
        ['La métamorphose en pies', 'les Piérides sont devenues des pies'],
        ['Le concours de chant', 'Le concours commence aussitôt'],
        ['La visite d\'une bergère', 'Elle prend l\'apparence d\'une bergère'],
        ['Les vantardises des Piérides', 'pour vanter leur propre talent'],
    ];
    const texteEntier = SUPPORT.pages.map((p) => p.content).join('\n\n');
    const places = evenements.map(([, passage]) => texteEntier.indexOf(passage));
    garde(places.every((p) => p >= 0), 'Q10 : un évènement introuvable dans le texte');
    const rangs = places.map((p) => places.filter((q) => q < p).length);
    garde(rangs.join() === '3,2,1,0', `Q10 : lignes dans l'ordre inverse du récit attendues, rangs ${rangs.join()}`);
    const payload = makeVraiFaux(evenements.map(([ligne]) => ligne), rangs,
        '<p>Remettre les évènements dans l\'ordre du texte.</p>', { ...opts(L10), colonnes: COLONNES_Q10 });
    // Un rang par évènement, comme l'item que le quiz reconstruit depuis sa bonne réponse (type 'association' :
    // cocher un rang le retire de la ligne qui l'avait) ; makeVraiFaux pose 'vraiFaux', qui laisserait deux 1ᵉʳ.
    const table = payload.elements_additionnels.elements.find((e) => e.type === 'table');
    table.config = { ...table.config, type: 'association' };
    return avecSupportLecture(payload, SUPPORT);
};

const GENERATORS = {
    q01_qcm_unique: genQ01,
    q02_qcm_unique: genQ02,
    q03_qcm_unique: genQ03,
    q04_qcm_unique: genQ04,
    q05_qcm_unique: genQ05,
    q06_qcm_unique: genQ06,
    q07_tableau: genQ07,
    q08_qcm_unique: genQ08,
    q09_qcm_unique: genQ09,
    q10_tableau: genQ10,
};

/** Une variante par item, sur le nouveau texte : l'alerte de variété du harnais est attendue. */
export const VARIANTES_FIXES = true;

export const ITEMS = [
    { questionNum: 'Q01', formatKey: 'q01_qcm_unique', libelle: L01, bonneReponseOfficielle: 'La punition d’une orgueilleuse' },
    { questionNum: 'Q02', formatKey: 'q02_qcm_unique', libelle: L02, bonneReponseOfficielle: 'tisser.' },
    { questionNum: 'Q03', formatKey: 'q03_qcm_unique', libelle: L03, bonneReponseOfficielle: 'vieille femme.' },
    { questionNum: 'Q04', formatKey: 'q04_qcm_unique', libelle: L04, bonneReponseOfficielle: 'de demander pardon.' },
    { questionNum: 'Q05', formatKey: 'q05_qcm_unique', libelle: L05, bonneReponseOfficielle: 'sa détermination.' },
    { questionNum: 'Q06', formatKey: 'q06_qcm_unique', libelle: L06, bonneReponseOfficielle: 'les dieux.' },
    {
        questionNum: 'Q07',
        formatKey: 'q07_tableau',
        libelle: L07,
        bonneReponseOfficielle: '[Arachné a lancé un défi à une déesse.|Vrai][Arachné a déchiré la toile de Minerve.|Faux]'
            + '[Arachné a représenté la puissance des dieux.|Faux][Arachné a brodé une toile parfaite.|Vrai]',
    },
    { questionNum: 'Q08', formatKey: 'q08_qcm_unique', libelle: L08, bonneReponseOfficielle: 'être une araignée.' },
    { questionNum: 'Q09', formatKey: 'q09_qcm_unique', libelle: L09, bonneReponseOfficielle: 'tissent des toiles.' },
    {
        questionNum: 'Q10',
        formatKey: 'q10_tableau',
        libelle: L10,
        bonneReponseOfficielle: '[Les vantardises d’Arachné|1ᵉʳ évènement][La visite d’une vieille femme|2ᵉ évènement]'
            + '[Le concours de tissage|3ᵉ évènement][La métamor-phose en araignée|4ᵉ évènement]',
    },
];

export const CATEGORIES = ITEMS.map(({ questionNum, formatKey, libelle }) => ({
    id: questionNum.toLowerCase(),
    label: `${questionNum} · ${libelle}`,
    formatsList: [{ key: formatKey, label: formatKey.endsWith('_tableau') ? 'Tableau' : 'QCM' }],
}));

export const generate = (typeExercice) => {
    const key = GENERATORS[typeExercice] ? typeExercice : randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
